import { getCookies, handleAutoDownload } from './helpers.js';

/**
 * GatedContentContainer class.
 * Handles showing/hiding pre-registration and post-registration blocks for a single container.
 */
class GatedContentContainer {
	/**
	 * Create a new GatedContentContainer instance.
	 *
	 * @param {HTMLElement} containerElement The container element.
	 */
	constructor(containerElement) {
		this.postType = window.gatedContent.postType;
		this.nonce = window.gatedContent.nonce;
		this.container = containerElement;
		this.getBlocks();
	}

	/**
	 * Get the blocks for the container.
	 */
	getBlocks() {
		this.formBlocks = this.container.querySelectorAll('.wp-block-em-components-gated-content-form');
		this.preRegistrationBlocks = this.container.querySelectorAll('.wp-block-em-components-gated-content-pre-registration');
		this.postRegistrationBlocks = this.container.querySelectorAll('.wp-block-em-components-gated-content-post-registration, .wp-block-em-components-gated-content-resource');
	}

	/**
	 * Initialize the container based on cookie status.
	 *
	 * @return {Promise<void>}
	 */
	async init() {
		// If user is cookied, load post-registration content.
		if (this.hasAllRequiredBlocks() && this.isCookieSet() && this.nonce) {
			try {
				const html = await this.fetchGatedContent();

				// Validate that we received HTML content.
				if (!html) {
					throw new Error('Invalid response: empty HTML content');
				}

				// Update post-registration blocks with fetched content.
				this.injectGatedContent(html);

				// Show post-registration content.
				this.showGatedContent();
			} catch (error) {
				// Log error but don't break the page.
				console.error('Error fetching gated content:', error);

				// On error, default to showing pre-registration and form, hiding post-registration.
				this.hideGatedContent();
			}
		} else {
			// User is not cookied - set default state.
			this.hideGatedContent();
		}
	}

	/**
	 * Check if the user has the required cookie set.
	 * For "access to all": gated_{postType}. For "per individual": gated_{postType}_{postID}.
	 *
	 * @return {boolean} True if the cookie is set.
	 */
	isCookieSet() {
		const cookies = getCookies();
		const postID = window.gatedContent?.postID;
		return Object.keys(cookies).some((key) => {
			if (key === `gated_${this.postType}`) return true;
			if (key === `gated_${this.postType}_posts`) return true; // Legacy array cookie.
			if (postID && key === `gated_${this.postType}_${postID}`) return true;
			return false;
		});
	}

	/**
	 * Check if this container has all required blocks.
	 *
	 * @return {boolean} True if all required blocks are present.
	 */
	hasAllRequiredBlocks() {
		return this.formBlocks.length > 0 && this.postRegistrationBlocks.length > 0;
	}

	/**
	 * Fade in the container.
	 */
	show() {
		this.container.classList.add('loaded');
	}

	/**
	 * Set default state (pre-reg visible, form visible, post-reg and resource hidden).
	 */
	hideGatedContent() {
		this.preRegistrationBlocks.forEach((block) => {
			block.style.display = '';
		});
		this.postRegistrationBlocks.forEach((block) => {
			block.style.display = 'none';
		});

		this.show();
	}

	/**
	 * Show post-registration and resource content and hide pre-registration and form.
	 */
	showGatedContent() {
		this.preRegistrationBlocks.forEach((block) => {
			block.style.display = 'none';
		});
		this.postRegistrationBlocks.forEach((block) => {
			block.style.display = 'block';
		});

		this.show();
	}

	/**
	 * Fetch gated content.
	 *
	 * @return {Promise<string>} The HTML content.
	 */
	async fetchGatedContent() {
		// Fetch updated content with cache buster.
		const cacheBuster = Math.random().toString(36).substring(2, 15);
		const response = await fetch(`${window.location.href}?gc=${cacheBuster}`, {
			headers: {
				'X-WP-Nonce': this.nonce,
			},
		});

		// Check if response is successful.
		if (!response.ok) {
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const html = await response.text();

		return html;
	}

	/**
	 * Update post-registration and resource blocks with fetched content.
	 *
	 * @param {string} html The HTML content to parse.
	 */
	injectGatedContent(html) {
		// Parse HTML into actual elements.
		const elements = document.createElement('div');
		elements.innerHTML = html;

		// Get all post registration and resource blocks from the fetched content.
		const fetchedContainer = elements.querySelector(`.wp-block-em-components-gated-content-container[id="${this.container.id}"]`);

		if (fetchedContainer) {
			this.container.innerHTML = fetchedContainer.innerHTML;
			this.getBlocks();
		}

		// Clean up temporary element.
		elements.remove();
	}
}

/**
 * Initialize gated content functionality.
 * Handles showing/hiding pre-registration and post-registration blocks based on cookie status.
 */
async function initGatedContent() {
	// Early return if gatedContent object is not available.
	if (!window.gatedContent) {
		console.warn('Gated content configuration not found. Skipping initialization.');
		return;
	}

	// Handle auto-download if triggered.
	handleAutoDownload();

	// Get gated content container blocks.
	const containerElements = [...document.querySelectorAll('.wp-block-em-components-gated-content-container')];

	// Early return if no container blocks are present - container blocks are required.
	if (containerElements.length === 0) {
		return;
	}

	// Initialize each container.
	const containers = containerElements.map(
		(container) => new GatedContentContainer(container),
	);

	// Initialize all containers in parallel.
	await Promise.all(
		containers.map((container) => container.init()),
	);
}

// Initialize when DOM is ready.
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', initGatedContent);
} else {
	// DOM is already ready.
	initGatedContent();
}
